import React, { useState, useEffect } from 'react';
import { Zap, Clock } from 'lucide-react';
import { auth } from '../../firebase';
import logger from '../../utils/logger';

const ActiveBoostIndicator = ({ compact = false }) => {
  const [activeBoost, setActiveBoost] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    if (!auth.currentUser) return;

    loadActiveBoost();
    const interval = setInterval(loadActiveBoost, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [auth.currentUser]);

  useEffect(() => {
    if (!activeBoost) return;

    const updateTimer = () => {
      const now = Date.now();
      if (activeBoost.endTime > now) {
        const remaining = activeBoost.endTime - now;
        const hours = Math.floor(remaining / 3600000);
        const minutes = Math.floor((remaining % 3600000) / 60000);
        const seconds = Math.floor((remaining % 60000) / 1000);

        if (hours > 0) {
          setTimeRemaining(`${hours}h ${minutes}m`);
        } else {
          setTimeRemaining(`${minutes}m ${seconds}s`);
        }
      } else {
        setActiveBoost(null);
        setTimeRemaining('');
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [activeBoost]);

  const loadActiveBoost = async () => {
    try {
      const { getDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../../firebase');

      const userBoostsRef = doc(db, 'userBoosts', auth.currentUser.uid);
      const boostsDoc = await getDoc(userBoostsRef);

      if (boostsDoc.exists()) {
        const boosts = boostsDoc.data().activeBoosts || [];
        const now = Date.now();

        // Find active XP boost
        const active = boosts.find(boost =>
          boost.status === 'active' &&
          boost.endTime > now &&
          boost.type.startsWith('xp_')
        );

        setActiveBoost(active || null);
      }
    } catch (error) {
      logger.error('Error loading active boost:', error);
    }
  };

  if (!activeBoost) return null;

  const getMultiplier = () => {
    if (activeBoost.type.includes('2x')) return '2x';
    if (activeBoost.type.includes('3x')) return '3x';
    if (activeBoost.type.includes('5x')) return '5x';
    return activeBoost.multiplier + 'x';
  };

  const getColor = () => {
    if (activeBoost.type.includes('2x')) return 'from-yellow-500 to-orange-500';
    if (activeBoost.type.includes('3x')) return 'from-orange-500 to-red-500';
    if (activeBoost.type.includes('5x')) return 'from-purple-500 to-pink-500';
    return 'from-blue-500 to-cyan-500';
  };

  if (compact) {
    // Compact floating badge version
    return (
      <div className="fixed bottom-20 right-4 z-40 animate-pulse">
        <div className={`bg-gradient-to-r ${getColor()} rounded-full px-4 py-2 shadow-lg flex items-center gap-2`}>
          <Zap className="w-4 h-4 text-white" fill="white" />
          <div className="text-white font-bold text-sm">
            {getMultiplier()} XP
          </div>
          <div className="text-white/90 text-xs">
            {timeRemaining}
          </div>
        </div>
      </div>
    );
  }

  // Full version for Level Up tab
  return (
    <div className={`bg-gradient-to-r ${getColor()} rounded-xl p-4 shadow-lg mb-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-2 rounded-lg">
            <Zap className="w-6 h-6 text-white" fill="white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">
              {getMultiplier()} XP Boost Active!
            </h3>
            <p className="text-white/90 text-sm">
              You're earning {getMultiplier()} XP on all predictions
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 text-white">
            <Clock className="w-5 h-5" />
            <span className="font-bold text-lg">{timeRemaining}</span>
          </div>
          <div className="text-white/80 text-xs">remaining</div>
        </div>
      </div>
    </div>
  );
};

export default ActiveBoostIndicator;
